import { makeStyles, mergeClasses } from '@fluentui/react-components';
import { CheckCircle, KeyboardReturn } from '@material-symbols-svg/react/rounded/w500';
import { memo, useCallback } from 'react';

import { palette, tokens } from '../../theme';

export type SearchResultRowProps = {
    id: number;
    title: string;
    accent: string;
    typeLabel: string;
    selected: boolean;
    onSelect: () => void;
};

export const SearchResultRow = memo(function SearchResultRow({ id, title, accent, typeLabel, selected, onSelect }: SearchResultRowProps) {
    const classes = useStyles();
    const handleClick = useCallback(() => onSelect(), [onSelect]);

    return (
        <button
            type="button"
            className={mergeClasses(classes.root, selected && classes.selected)}
            onClick={handleClick}
            disabled={selected}
            aria-disabled={selected}
            title={selected ? 'Already in plan' : undefined}
        >
            <span className={classes.dot} style={{ backgroundColor: accent }} aria-label={typeLabel} />
            <span className={classes.idText}>#{id}</span>
            <span className={classes.title}>{title}</span>
            {selected ? (
                <span className={classes.addedBadge}>
                    <CheckCircle className={classes.addedIcon} aria-hidden />
                    <span>Added</span>
                </span>
            ) : (
                <KeyboardReturn className={classes.returnIcon} aria-hidden />
            )}
        </button>
    );
});

const useStyles = makeStyles({
    root: {
        display: 'grid',
        gridTemplateColumns: '8px 64px minmax(0, 1fr) auto',
        alignItems: 'center',
        gap: tokens.spacingHorizontalM,
        width: '100%',
        marginBottom: '8px',
        padding: '12px 14px',
        backgroundColor: palette.panelBg,
        border: `1px solid ${palette.cardBorder}`,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'left',
        cursor: 'pointer',
        font: 'inherit',
        '&:hover': {
            backgroundColor: palette.panelMuted,
        },
    },
    selected: {
        backgroundColor: palette.accentBg,
        border: `1px solid ${palette.accentBgStrong}`,
        cursor: 'default',
        '&:hover': {
            backgroundColor: palette.accentBg,
        },
    },
    addedBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '12px',
        fontWeight: 500,
        color: palette.accentText,
        flexShrink: 0,
    },
    addedIcon: {
        width: '14px',
        height: '14px',
    },
    dot: {
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        flexShrink: 0,
    },
    idText: {
        fontSize: '12px',
        color: tokens.colorNeutralForeground3,
        whiteSpace: 'nowrap',
    },
    title: {
        fontSize: '13px',
        color: tokens.colorNeutralForeground1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
    },
    returnIcon: {
        width: '14px',
        height: '14px',
        color: palette.dimText,
        flexShrink: 0,
    },
});
